import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
  Paper,
  Button
} from '@mui/material';
import { format, isSameDay, parseISO, startOfDay } from 'date-fns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from '../utils/axios';

const ReceivedBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list');
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));

  useEffect(() => {
    const fetchReceivedBookings = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/bookings/received');
        console.log('Received bookings:', response.data); // Debug log
        setBookings(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching received bookings:', err);
        setError('Failed to load received bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchReceivedBookings();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await axios.patch(`/api/bookings/${bookingId}/status`, { status: newStatus });
      setBookings(bookings.map(booking => 
        booking._id === bookingId ? { ...booking, status: newStatus } : booking
      ));
    } catch (err) {
      console.error('Error updating booking status:', err);
      setError('Failed to update booking status');
    }
  };

  const getBookingsForDate = (date) => {
    const startOfSelectedDate = startOfDay(date);
    const endOfSelectedDate = new Date(startOfSelectedDate);
    endOfSelectedDate.setHours(23, 59, 59, 999);

    console.log('Getting bookings for date range:', {
      start: startOfSelectedDate,
      end: endOfSelectedDate
    });

    const bookingsForDate = bookings.filter(booking => {
      const bookingStart = new Date(booking.startTime);
      const bookingEnd = new Date(booking.endTime);
      
      console.log('Comparing booking:', {
        bookingStart,
        bookingEnd,
        selectedStart: startOfSelectedDate,
        selectedEnd: endOfSelectedDate
      });

      // Check if booking overlaps with the selected date
      return (
        (bookingStart <= endOfSelectedDate && bookingEnd >= startOfSelectedDate)
      );
    });

    console.log('Found bookings:', bookingsForDate);
    return bookingsForDate;
  };

  const handleViewChange = (event, newView) => {
    setView(newView);
  };

  const renderBookingCard = (booking) => (
    <Card key={booking._id} sx={{ mb: 2 }}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            {booking.gymId?.photos?.[0] && (
              <Box
                component="img"
                src={booking.gymId.photos[0]}
                alt={booking.gymId.name}
                sx={{
                  width: '100%',
                  height: 200,
                  objectFit: 'cover',
                  borderRadius: 1
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom>
              {booking.gymId?.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Booked by: {booking.userId?.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {format(new Date(booking.startTime), 'MMM d, yyyy h:mm a')} - {format(new Date(booking.endTime), 'h:mm a')}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={booking.status.toUpperCase()}
                color={getStatusColor(booking.status)}
                size="small"
              />
              <Chip
                label={`$${booking.totalPrice?.toFixed(2) || '0.00'}`}
                color="primary"
                size="small"
              />
            </Box>
            {booking.status === 'pending' && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => handleStatusUpdate(booking._id, 'confirmed')}
                >
                  Confirm
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => handleStatusUpdate(booking._id, 'cancelled')}
                >
                  Cancel
                </Button>
              </Box>
            )}
            {/* Contact Support button for all bookings */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="info"
                size="small"
                onClick={async () => {
                  try {
                    const response = await axios.post(`/api/bookings/${booking._id}/contact-support`);
                    alert(response.data.message);
                  } catch (err) {
                    console.error('Error contacting support:', err);
                    alert('Failed to contact support. Please try again.');
                  }
                }}
              >
                Contact Support
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Booked on {format(new Date(booking.createdAt), 'MMM d, yyyy')}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderDay = (day, selectedDays, pickersDayProps) => {
    const startOfDayDate = startOfDay(day);
    const endOfDayDate = new Date(startOfDayDate);
    endOfDayDate.setHours(23, 59, 59, 999);

    const hasBookings = bookings.some(booking => {
      const bookingStart = new Date(booking.startTime);
      const bookingEnd = new Date(booking.endTime);
      return (
        (bookingStart <= endOfDayDate && bookingEnd >= startOfDayDate)
      );
    });

    return (
      <Box
        {...pickersDayProps}
        sx={{
          ...(pickersDayProps?.sx || {}),
          ...(hasBookings && {
            backgroundColor: 'error.main',
            color: 'error.contrastText',
            '&:hover': {
              backgroundColor: 'error.dark',
            },
            '&.Mui-selected': {
              backgroundColor: 'error.dark',
            },
          }),
        }}
      />
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Received Bookings
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={view} onChange={handleViewChange}>
          <Tab label="List View" value="list" />
          <Tab label="Calendar View" value="calendar" />
        </Tabs>
      </Box>

      {bookings.length === 0 ? (
        <Alert severity="info">No bookings received yet.</Alert>
      ) : view === 'list' ? (
        <Grid container spacing={3}>
          {bookings.map(booking => (
            <Grid item xs={12} key={booking._id}>
              {renderBookingCard(booking)}
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Received Bookings Calendar
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar
                  value={selectedDate}
                  onChange={(newDate) => {
                    console.log('Selected new date:', newDate);
                    setSelectedDate(startOfDay(newDate));
                  }}
                  slots={{
                    day: renderDay
                  }}
                />
              </LocalizationProvider>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom>
              Bookings for {format(selectedDate, 'MMMM d, yyyy')}
            </Typography>
            {getBookingsForDate(selectedDate).length === 0 ? (
              <Alert severity="info">No bookings for this date.</Alert>
            ) : (
              getBookingsForDate(selectedDate).map(booking => renderBookingCard(booking))
            )}
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default ReceivedBookings; 